import { useState } from 'react';
import { Mail, Lock, User, Loader2, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Google Logo SVG Component
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export function LoginScreen() {
  const { login, loginWithGoogle, register, resetPassword } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } catch (err) {
      setError(err.message || 'Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (err) {
      setError(err.message || 'Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setResetEmailSent(false);
    setError('');
  };

  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      await loginWithGoogle();
    } catch (err) {
      // Don't show error for user cancellation
      if (err.message !== 'Login cancelado' && err.message !== 'Operação cancelada') {
        setError(err.message || 'Erro ao entrar com Google');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="mb-8 text-center">
        <img 
          src="/logo192.png" 
          alt="Dealer Manager" 
          className="w-20 h-20 rounded-2xl mx-auto mb-4 shadow-lg"
        />
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Dealer Manager</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Gestão de Revendas</p>
      </div>

      {/* Form Card */}
      <div className="w-full max-w-sm bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-6 shadow-lg">
        
        {/* Forgot Password Flow */}
        {showForgotPassword ? (
          resetEmailSent ? (
            // Success state - email sent
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
                Email Enviado!
              </h2>
              <p className="text-sm text-[var(--text-secondary)] mb-6">
                Enviamos um link de recuperação para <strong className="text-[var(--text-primary)]">{email}</strong>. 
                Verifique sua caixa de entrada e spam.
              </p>
              <button
                type="button"
                onClick={backToLogin}
                className="w-full h-12 bg-[var(--accent-primary)] text-white rounded-lg font-medium flex items-center justify-center gap-2 active:opacity-90 transition-all"
                data-testid="btn-back-to-login"
              >
                <ArrowLeft size={18} />
                Voltar para Login
              </button>
            </div>
          ) : (
            // Forgot password form
            <>
              <button
                type="button"
                onClick={backToLogin}
                className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4 transition-colors"
                data-testid="btn-back"
              >
                <ArrowLeft size={16} />
                Voltar
              </button>
              
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
                Esqueci minha senha
              </h2>
              <p className="text-sm text-[var(--text-secondary)] mb-6">
                Digite seu email e enviaremos um link para redefinir sua senha.
              </p>

              <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
                {/* Email */}
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="E-mail cadastrado"
                    required
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
                    data-testid="input-reset-email"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <p className="text-sm text-red-500 text-center bg-red-500/10 rounded-lg py-2 px-3">
                    {error}
                  </p>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[var(--accent-primary)] text-white rounded-lg font-medium flex items-center justify-center gap-2 active:opacity-90 disabled:opacity-50 transition-all"
                  data-testid="btn-send-reset"
                >
                  {loading ? (
                    <><Loader2 size={20} className="animate-spin" /> Enviando...</>
                  ) : (
                    'Enviar Link de Recuperação'
                  )}
                </button>
              </form>
            </>
          )
        ) : (
          // Login/Register Form
          <>
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6 text-center">
              {isLogin ? 'Entrar' : 'Criar Conta'}
            </h2>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Name (only for register) */}
              {!isLogin && (
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome"
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
                    data-testid="input-name"
                  />
                </div>
              )}

              {/* Email */}
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-mail"
                  required
                  className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
                  data-testid="input-email"
                />
              </div>

              {/* Password */}
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha"
                  required
                  minLength={6}
                  className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-12 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Forgot Password Link - Only show on login */}
              {isLogin && (
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setError('');
                  }}
                  className="text-sm text-[var(--accent-primary)] hover:underline text-right -mt-2"
                  data-testid="btn-forgot-password"
                >
                  Esqueci minha senha
                </button>
              )}

              {/* Error Message */}
              {error && (
                <p className="text-sm text-red-500 text-center bg-red-500/10 rounded-lg py-2 px-3">
                  {error}
                </p>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || googleLoading}
                className="w-full h-12 bg-[var(--accent-primary)] text-white rounded-lg font-medium flex items-center justify-center gap-2 active:opacity-90 disabled:opacity-50 transition-all"
                data-testid="btn-submit"
              >
                {loading ? (
                  <><Loader2 size={20} className="animate-spin" /> Aguarde...</>
                ) : (
                  isLogin ? 'Entrar' : 'Criar Conta'
                )}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-[var(--border-color)]"></div>
                <span className="text-xs text-[var(--text-muted)]">ou</span>
                <div className="flex-1 h-px bg-[var(--border-color)]"></div>
              </div>

              {/* Google Sign-In Button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading || googleLoading}
                className="w-full h-12 bg-white border border-[var(--border-color)] rounded-lg font-medium flex items-center justify-center gap-3 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 transition-all text-gray-700"
                data-testid="btn-google-login"
              >
                {googleLoading ? (
                  <><Loader2 size={20} className="animate-spin text-gray-500" /> Conectando...</>
                ) : (
                  <>
                    <GoogleLogo />
                    <span>Entrar com Google</span>
                  </>
                )}
              </button>
            </form>

            {/* Toggle Login/Register */}
            <div className="mt-6 text-center">
              <p className="text-sm text-[var(--text-secondary)]">
                {isLogin ? 'Não tem conta?' : 'Já tem conta?'}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                  }}
                  className="ml-1 text-[var(--accent-primary)] font-medium hover:underline"
                  data-testid="btn-toggle-mode"
                >
                  {isLogin ? 'Criar agora' : 'Entrar'}
                </button>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Firebase Auth Info */}
      <div className="mt-6 text-center text-xs text-[var(--text-muted)] max-w-xs">
        <p className="mb-1">🔐 Autenticação segura via Firebase</p>
        <p>Use seu e-mail completo para entrar</p>
      </div>
    </div>
  );
}
